// Presentational building blocks for the Relationship drawer, styled after
// the Relationship Layer design mock: glowing-dot section cards, pill
// filters, left-border type tags, confidence bars, banners and stat tiles.
import type { ReactNode } from "react";
import {
  AlertTriangle,
  Check,
  Flag,
  Link2,
  Loader2,
  OctagonX,
  Search,
  X,
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { toTitleCaseFromSnake } from "../utils";
import {
  REL_BANNER_TONE_HEX,
  REL_COLOR,
  REL_FILTER_ACTIVE_CLS,
  REL_FOCUS_RING_CLS,
  REL_INPUT_FOCUS_CLS,
  REL_TAG_TONE_HEX,
  RELATIONSHIP_COPY,
  RELATIONSHIP_SEARCH_MAX_LENGTH,
  RELATIONSHIP_STATUS_TAG_META,
  type RelBannerTone,
  type RelTagTone,
} from "./utilities/constants";
import { confidenceHex, pct, typeHex, typeLabel } from "./utilities/helpers";

/* ── Section card ────────────────────────────────────────────────── */

export function RelCard({
  title,
  dot,
  right,
  children,
  flush = false,
}: {
  title: string;
  dot: string;
  right?: ReactNode;
  children: ReactNode;
  flush?: boolean;
}) {
  return (
    <section className="rel-anim overflow-hidden rounded-[10px] border border-border bg-card">
      <div className="flex flex-col gap-1 border-b border-border px-[18px] py-3.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div className="flex items-center gap-2">
          <span
            className="size-2 flex-none rounded-full"
            style={{ background: dot, boxShadow: `0 0 6px ${dot}` }}
          />
          <span className="text-sm font-bold text-foreground">{title}</span>
        </div>
        {right && <div className="text-xs text-muted-foreground break-words sm:text-right">{right}</div>}
      </div>
      <div className={flush ? "" : "p-[18px]"}>{children}</div>
    </section>
  );
}

/* ── Filters ─────────────────────────────────────────────────────── */

export function FilterPill({
  active,
  label,
  count,
  dot,
  onClick,
}: {
  active: boolean;
  label: string;
  count?: number;
  dot?: string;
  onClick: () => void;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={onClick}
      aria-pressed={active}
      className={`h-auto select-none rounded-[14px] border px-3 py-[5px] text-[11px] font-semibold transition-colors ${REL_FOCUS_RING_CLS} ${active
          ? REL_FILTER_ACTIVE_CLS
          : "border-border bg-muted/40 text-muted-foreground hover:border-foreground/25 hover:text-foreground"
        }`}
    >
      {dot && <span className="mr-1 inline-block size-[5px] rounded-full" style={{ background: dot }} />}
      {label}
      {typeof count === "number" && (
        <span className="ml-[5px] text-[10px] tabular-nums opacity-60">{count}</span>
      )}
    </Button>
  );
}

export function SearchBox({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  return (
    <div className="relative w-full sm:ml-auto sm:w-[220px]">
      <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value.slice(0, RELATIONSHIP_SEARCH_MAX_LENGTH))}
        maxLength={RELATIONSHIP_SEARCH_MAX_LENGTH}
        placeholder={placeholder}
        aria-label={placeholder}
        className={`h-8 rounded-lg bg-muted/40 pl-8 text-xs ${REL_INPUT_FOCUS_CLS}`}
      />
    </div>
  );
}

/* ── Tags ────────────────────────────────────────────────────────── */

const TAG_BASE =
  "inline-flex items-center gap-[3px] whitespace-nowrap rounded px-2 py-0.5 text-[10px] font-bold tracking-[0.2px]";

const STATUS_TAG_ICON = {
  resolved: Check,
  review: Flag,
  other: X,
} as const;

export function StatusTag({ status }: { status: string }) {
  const meta = RELATIONSHIP_STATUS_TAG_META[status];
  const kind = meta?.kind ?? "other";
  const Icon = STATUS_TAG_ICON[kind];
  const label = meta?.label ?? toTitleCaseFromSnake(status);
  const hex = meta?.hex ?? REL_COLOR.red;

  if (kind === "review") {
    return (
      <span
        className={`${TAG_BASE} border-l-[3px] bg-muted font-semibold text-muted-foreground`}
        style={{ borderLeftColor: hex }}
      >
        <Icon className="size-3" /> {label}
      </span>
    );
  }

  return (
    <span className={TAG_BASE} style={{ background: `${hex}1f`, color: hex }}>
      <Icon className="size-3" /> {label}
    </span>
  );
}

export function TypeTag({ type, strong = false }: { type: string; strong?: boolean }) {
  return (
    <span
      className={`${TAG_BASE} border-l-[3px] bg-muted text-muted-foreground ${strong ? "font-bold" : "font-semibold"}`}
      style={{ borderLeftColor: typeHex(type) }}
    >
      {typeLabel(type)}
    </span>
  );
}

export function ConfidenceBar({
  value,
  color,
}: {
  value: number | null;
  color?: string;
}) {
  const p = pct(value);
  const c = color ?? confidenceHex(value);
  return (
    <span className="inline-flex items-center gap-1">
      <span className="h-[3px] w-10 overflow-hidden rounded-sm bg-muted">
        <span className="block h-full rounded-sm" style={{ width: `${p}%`, background: c }} />
      </span>
      <span className="min-w-[28px] text-[10px] font-bold tabular-nums" style={{ color: c }}>
        {value === null ? "-" : `${p}%`}
      </span>
    </span>
  );
}

/* ── Header ──────────────────────────────────────────────────────── */

function tagToneStyle(tone: RelTagTone | undefined) {
  if (!tone) return undefined;
  const hex = REL_TAG_TONE_HEX[tone];
  return { background: `${hex}1f`, color: hex, borderColor: "transparent" };
}

export function RelationshipHeader({
  documentTitle,
  statusLabel,
  statusTone,
  tags,
}: {
  documentTitle?: string;
  statusLabel: string;
  statusTone: RelTagTone;
  tags: { label: string; tone?: RelTagTone }[];
}) {
  const statusColor = REL_TAG_TONE_HEX[statusTone] ?? REL_COLOR.green;

  return (
    <header className="sticky top-0 z-10 border-b border-border bg-card px-4 py-4 pr-14 sm:px-6 sm:pr-16">
      <div className="flex items-start gap-4">
        <div
          className="flex size-11 flex-none items-center justify-center rounded-xl border border-border"
          style={{ background: `${REL_COLOR.accent}1f` }}
        >
          <Link2 className="size-5" style={{ color: REL_COLOR.accent }} />
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-[22px] font-extrabold leading-tight tracking-[-0.3px] text-foreground">
              {RELATIONSHIP_COPY.title}
            </h2>
          </div>
          {documentTitle && (
            <span className="mt-0.5 text-[13px] font-semibold text-foreground/90 break-words">
              {documentTitle}
            </span>
          )}
          <p className="mt-1 hidden max-w-[700px] text-[13px] leading-normal text-muted-foreground break-words sm:block">
            {RELATIONSHIP_COPY.description}
          </p>
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            <span
              className="rounded-[14px] px-2.5 py-[3px] text-[11px] font-semibold"
              style={{ background: `${statusColor}1f`, color: statusColor }}
            >
              {statusLabel}
            </span>
            {tags.map((tag) => (
              <span
                key={tag.label}
                className="rounded-[14px] border border-border bg-muted px-2.5 py-[3px] text-[11px] font-semibold text-muted-foreground"
                style={tagToneStyle(tag.tone)}
              >
                {tag.label}
              </span>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
}

/* ── Banners ─────────────────────────────────────────────────────── */

const REL_BANNER_ICON = {
  red: OctagonX,
  amber: AlertTriangle,
  green: Check,
} as const;

export function RelBanner({
  tone,
  children,
}: {
  tone: RelBannerTone;
  children: ReactNode;
}) {
  const color = REL_BANNER_TONE_HEX[tone];
  const Icon = REL_BANNER_ICON[tone];
  return (
    <div
      className="rel-anim flex items-start gap-2 rounded-[10px] border px-[18px] py-3 text-[13px] font-medium leading-relaxed"
      style={{ background: `${color}1f`, color, borderColor: `${color}33` }}
    >
      <Icon className="mt-0.5 size-4 flex-none" />
      <span className="min-w-0 break-words">{children}</span>
    </div>
  );
}

/* ── Stat tiles ──────────────────────────────────────────────────── */

export function StatTiles({ tiles }: { tiles: { label: string; value: number; color: string }[] }) {
  return (
    <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-5">
      {tiles.map((tile) => (
        <div
          key={tile.label}
          className="rounded-lg border border-border bg-muted/40 px-3 py-3.5 text-center transition-colors hover:border-foreground/25"
        >
          <div className="text-[26px] font-extrabold leading-none tabular-nums" style={{ color: tile.color }}>
            {tile.value}
          </div>
          <div className="mt-1.5 text-[10px] font-semibold tracking-[0.4px] text-muted-foreground">
            {tile.label}
          </div>
        </div>
      ))}
    </div>
  );
}

export function RelationshipLoading() {
  return (
    <div className="flex flex-1 items-center justify-center gap-2 text-muted-foreground">
      <Loader2 className="size-5 animate-spin" />
      <span className="text-sm">{RELATIONSHIP_COPY.loadingLabel}</span>
    </div>
  );
}

export function RelEmpty({ label }: { label: string }) {
  return <div className="px-4 py-8 text-center text-[13px] text-muted-foreground">{label}</div>;
}

/** Fade-up entrance matching the mock's `.anim` keyframes. */
export function RelKeyframes() {
  return (
    <style>{`
@keyframes rel-fade-in { from { opacity: 0; transform: translateY(6px) } to { opacity: 1; transform: translateY(0) } }
.rel-anim { animation: rel-fade-in .3s ease both }
.rel-anim:nth-child(2) { animation-delay: .04s }
.rel-anim:nth-child(3) { animation-delay: .08s }
.rel-anim:nth-child(4) { animation-delay: .12s }
.rel-anim:nth-child(5) { animation-delay: .16s }
@keyframes rel-dash { to { stroke-dashoffset: -18 } }
.rel-dash-flow { animation: rel-dash 1.2s linear infinite }
@keyframes rel-comet { from { stroke-dashoffset: 100 } to { stroke-dashoffset: 0 } }
.rel-comet { animation: rel-comet 3.6s cubic-bezier(.45,0,.55,1) infinite }
@media (prefers-reduced-motion: reduce) {
  .rel-anim, .rel-dash-flow, .rel-comet { animation: none }
}
`}</style>
  );
}
